/**
 * AddCountryDialog - Modal para añadir un nuevo país al pasaporte
 * Estilo alineado con Dashboard: colores primarios indigo/púrpura, tokens y tipografía.
 */

import React, { useState } from "react";
import {
	Dialog,
	DialogContent,
	DialogActions,
	Button,
	TextField,
	Autocomplete,
	Box,
	Typography,
	Rating,
} from "@mui/material";
import { getFlag } from "./passportIconUtils";

/* Tokens alineados con Dashboard.css y ExploreSection */
const DASHBOARD = {
	primary: "#6366F1",
	primaryDark: "#4F46E5",
	textPrimary: "#1a1a2e",
	textMuted: "#64748B",
	textSecondary: "#4B5563",
	border: "rgba(0, 0, 0, 0.06)",
	radiusCard: "20px",
	radiusButton: "12px",
	shadowMd: "0 4px 16px rgba(0, 0, 0, 0.08)",
	shadowPrimary: "0 4px 16px rgba(99, 102, 241, 0.35)",
	bgCard: "#fff",
	bgSubtle: "rgba(0, 0, 0, 0.02)",
	primaryTint: "rgba(99, 102, 241, 0.12)",
};

// Lista de países con código ISO
const COUNTRIES = [
	{ code: "AF", name: "Afganistán" },
	{ code: "AL", name: "Albania" },
	{ code: "SA", name: "Arabia Saudí" },
	{ code: "DZ", name: "Argelia" },
	{ code: "WS", name: "Samoa" },
	{ code: "AD", name: "Andorra" },
	{ code: "AO", name: "Angola" },
	{ code: "AI", name: "Anguila" },
	{ code: "AQ", name: "Antártida" },
	{ code: "AG", name: "Antigua y Barbuda" },
	{ code: "AR", name: "Argentina" },
	{ code: "AM", name: "Armenia" },
	{ code: "AW", name: "Aruba" },
	{ code: "AU", name: "Australia" },
	{ code: "AT", name: "Austria" },
	{ code: "AZ", name: "Azerbaiyán" },

	// Países que ya tenías
	{ code: "BE", name: "Bélgica" },
	{ code: "BZ", name: "Belice" },
	{ code: "BO", name: "Bolivia" },
	{ code: "BA", name: "Bosnia y Herzegovina" },
	{ code: "BR", name: "Brasil" },
	{ code: "BG", name: "Bulgaria" },
	{ code: "KH", name: "Camboya" },
	{ code: "CA", name: "Canadá" },
	{ code: "VA", name: "Vaticano" },
	{ code: "CL", name: "Chile" },
	{ code: "CN", name: "China" },
	{ code: "CO", name: "Colombia" },
	{ code: "CR", name: "Costa Rica" },
	{ code: "HR", name: "Croacia" },
	{ code: "CU", name: "Cuba" },
	{ code: "DK", name: "Dinamarca" },
	{ code: "EC", name: "Ecuador" },
	{ code: "EG", name: "Egipto" },

	// Más países faltantes
	{ code: "SV", name: "El Salvador" },
	{ code: "GQ", name: "Guinea Ecuatorial" },
	{ code: "ER", name: "Eritrea" },
	{ code: "EE", name: "Estonia" },
	{ code: "ET", name: "Etiopía" },
	{ code: "PH", name: "Filipinas" },
	{ code: "FK", name: "Islas Malvinas" },
	{ code: "FO", name: "Islas Feroe" },
	{ code: "FJ", name: "Fiyi" },
	{ code: "GA", name: "Gabón" },
	{ code: "GM", name: "Gambia" },
	{ code: "GN", name: "Guinea" },
	{ code: "GW", name: "Guinea-Bissau" },
	{ code: "GY", name: "Guyana" },
	{ code: "HT", name: "Haití" },

	// Más países que ya tenías
	{ code: "HN", name: "Honduras" },
	{ code: "HK", name: "Hong Kong" },
	{ code: "HU", name: "Hungría" },
	{ code: "IN", name: "India" },
	{ code: "ID", name: "Indonesia" },
	{ code: "IQ", name: "Irak" },
	{ code: "IR", name: "Irán" },
	{ code: "IE", name: "Irlanda" },
	{ code: "IS", name: "Islandia" },
	{ code: "IL", name: "Israel" },
	{ code: "IT", name: "Italia" },

	// Más países faltantes
	{ code: "JM", name: "Jamaica" },
	{ code: "JE", name: "Jersey" },
	{ code: "JO", name: "Jordania" },
	{ code: "JP", name: "Japón" },
	{ code: "KG", name: "Kirguistán" },
	{ code: "KI", name: "Kiribati" },
	{ code: "KP", name: "Corea del Norte" },

	// Más países que ya tenías
	{ code: "KR", name: "Corea del Sur" },
	{ code: "KZ", name: "Kazajistán" },
	{ code: "KE", name: "Kenia" },
	{ code: "KW", name: "Kuwait" },
	{ code: "LA", name: "Laos" },
	{ code: "LV", name: "Letonia" },
	{ code: "LB", name: "Líbano" },
	{ code: "LI", name: "Liechtenstein" },
	{ code: "LT", name: "Lituania" },
	{ code: "LU", name: "Luxemburgo" },
	{ code: "MY", name: "Malasia" },
	{ code: "MV", name: "Maldivas" },
	{ code: "MT", name: "Malta" },
	{ code: "MA", name: "Marruecos" },
	{ code: "MW", name: "Malaui" },
	{ code: "MG", name: "Madagascar" },
	{ code: "MU", name: "Mauricio" },
	{ code: "MK", name: "Macedonia del Norte" },
	{ code: "MD", name: "Moldavia" },
	{ code: "MO", name: "Macao" },

	// Continúa incluyendo todos los países existentes...
	{ code: "MX", name: "México" },
	{ code: "MC", name: "Mónaco" },
	{ code: "MN", name: "Mongolia" },
	{ code: "ME", name: "Montenegro" },
	{ code: "MZ", name: "Mozambique" },
	{ code: "MM", name: "Myanmar" },
	{ code: "NA", name: "Namibia" },
	{ code: "NE", name: "Níger" },
	{ code: "NG", name: "Nigeria" },
	{ code: "NI", name: "Nicaragua" },
	{ code: "NP", name: "Nepal" },
	{ code: "NO", name: "Noruega" },
	{ code: "NZ", name: "Nueva Zelanda" },
	{ code: "OM", name: "Omán" },
	{ code: "NL", name: "Países Bajos" },

	// Continúa con Sudamérica, Europa, Asia, África
	{ code: "PK", name: "Pakistán" },
	{ code: "PG", name: "Papúa Nueva Guinea" },
	{ code: "PS", name: "Palestina" },
	{ code: "PY", name: "Paraguay" },
	{ code: "PE", name: "Perú" },
	{ code: "PL", name: "Polonia" },
	{ code: "PT", name: "Portugal" },
	{ code: "QA", name: "Catar" },
	{ code: "DO", name: "República Dominicana" },
	{ code: "RO", name: "Rumanía" },
	{ code: "RU", name: "Rusia" },
	{ code: "SM", name: "San Marino" },
	{ code: "RS", name: "Serbia" },
	{ code: "SC", name: "Seychelles" },
	{ code: "SL", name: "Sierra Leona" },
	{ code: "SG", name: "Singapur" },
	{ code: "SK", name: "Eslovaquia" },
	{ code: "SI", name: "Eslovenia" },
	{ code: "SO", name: "Somalia" },
	{ code: "ZA", name: "Sudáfrica" },
	{ code: "SS", name: "Sudán del Sur" },
	{ code: "ES", name: "España" },
	{ code: "LK", name: "Sri Lanka" },
	{ code: "SD", name: "Sudán" },
	{ code: "SE", name: "Suecia" },
	{ code: "CH", name: "Suiza" },
	{ code: "SY", name: "Siria" },
	{ code: "TJ", name: "Tayikistán" },
	{ code: "TH", name: "Tailandia" },
	{ code: "TZ", name: "Tanzania" },
	{ code: "TL", name: "Timor-Leste" },
	{ code: "TG", name: "Togo" },
	{ code: "TO", name: "Tonga" },
	{ code: "TT", name: "Trinidad y Tobago" },
	{ code: "TN", name: "Túnez" },
	{ code: "TR", name: "Turquía" },
	{ code: "TM", name: "Turkmenistán" },
	{ code: "TV", name: "Tuvalu" },
	{ code: "UG", name: "Uganda" },
	{ code: "UA", name: "Ucrania" },
	{ code: "UY", name: "Uruguay" },
	{ code: "UZ", name: "Uzbekistán" },
	{ code: "VE", name: "Venezuela" },
	{ code: "VN", name: "Vietnam" },
	{ code: "VU", name: "Vanuatu" },
	{ code: "YE", name: "Yemen" },
	{ code: "ZM", name: "Zambia" },
	{ code: "ZW", name: "Zimbabue" },
	{ code: "BB", name: "Barbados" },
	{ code: "BS", name: "Bahamas" },
	{ code: "DM", name: "Dominica" },
	{ code: "LC", name: "Santa Lucía" },
	{ code: "KN", name: "San Cristóbal y Nieves" },
	{ code: "VC", name: "San Vicente y las Granadinas" },
	{ code: "GD", name: "Granada" },
	{ code: "CY", name: "Chipre" },
	{ code: "PF", name: "Polinesia Francesa" },
	{ code: "TW", name: "Taiwán" },
	{ code: "PR", name: "Puerto Rico" },
	{ code: "PA", name: "Panamá" },
	{ code: "BW", name: "Botsuana" },
	{ code: "GT", name: "Guatemala" },
	{ code: "GE", name: "Georgia" },
	{ code: "CW", name: "Curazao" },
	{ code: "US", name: "Estados Unidos" },
	{ code: "AE", name: "Emiratos Árabes Unidos" },
	{ code: "CV", name: "Cabo Verde" },
	{ code: "GL", name: "Groenlandia" },
	{ code: "BY", name: "Bielorrusia" },
	{ code: "SR", name: "Surinam" },
	{ code: "BJ", name: "Benín" },
	{ code: "BF", name: "Burkina Faso" },
	{ code: "BI", name: "Burundi" },
	{ code: "CF", name: "República Centroafricana" },
	{ code: "CG", name: "República del Congo" },
	{ code: "CD", name: "República Democrática del Congo" },
	{ code: "DJ", name: "Yibuti" },
	{ code: "KM", name: "Comoras" },
	{ code: "LS", name: "Lesoto" },
	{ code: "LR", name: "Liberia" },
	{ code: "LY", name: "Libia" },
	{ code: "MR", name: "Mauritania" },
	{ code: "RW", name: "Ruanda" },
	{ code: "ST", name: "Santo Tomé y Príncipe" },
	{ code: "SZ", name: "Esuatini" },
	{ code: "BN", name: "Brunéi" },
	{ code: "NC", name: "Nueva Caledonia" },
	{ code: "FM", name: "Micronesia" },
	{ code: "MH", name: "Islas Marshall" },
	{ code: "NR", name: "Nauru" },
	{ code: "PW", name: "Palaos" },
	{ code: "SB", name: "Islas Salomón" },
	{ code: "GB", name: "Reino Unido" },
	{ code: "CI", name: "Costa de Marfil" },
	{ code: "BH", name: "Baréin" },
	{ code: "CK", name: "Islas Cook" },
	{ code: "NU", name: "Niue" },
	{ code: "SX", name: "Sint Maarten" },
	{ code: "MF", name: "San Martín (parte francesa)" },
	{ code: "BL", name: "San Bartolomé" },
	{ code: "BD", name: "Bangladés" },
	{ code: "BT", name: "Bután" },
	{ code: "CM", name: "Camerún" },
	{ code: "TD", name: "Chad" },
	{ code: "GH", name: "Ghana" },
	{ code: "GR", name: "Grecia" },
	{ code: "ML", name: "Malí" },
	{ code: "SN", name: "Senegal" },
	{ code: "RE", name: "Reunión" },
	{ code: "YT", name: "Mayotte" },
	{ code: "GP", name: "Guadalupe" },
	{ code: "MQ", name: "Martinica" },
	{ code: "GF", name: "Guayana Francesa" },
	{ code: "BM", name: "Bermudas" },
	{ code: "KY", name: "Islas Caimán" },
	{ code: "VG", name: "Islas Vírgenes Británicas" },
	{ code: "VI", name: "Islas Vírgenes de EE. UU." },
	{ code: "TC", name: "Islas Turcas y Caicos" },
	{ code: "IM", name: "Isla de Man" },
	{ code: "GG", name: "Guernsey" },
	{ code: "PM", name: "San Pedro y Miquelón" },
	{ code: "WF", name: "Wallis y Futuna" },
	{ code: "AS", name: "Samoa Americana" },
	{ code: "GU", name: "Guam" },
	{ code: "MP", name: "Islas Marianas del Norte" },
].sort((a, b) => a.name.localeCompare(b.name, "es"));

const AddCountryDialog = ({ open, onClose, onAdd }) => {
	const [selectedCountry, setSelectedCountry] = useState(null);
	const [city, setCity] = useState("");
	const [visitDate, setVisitDate] = useState("");
	const [notes, setNotes] = useState("");
	const [rating, setRating] = useState(0);
	const [loading, setLoading] = useState(false);

	const handleSubmit = async () => {
		if (!selectedCountry) return;
		setLoading(true);
		try {
			await onAdd({
				countryCode: selectedCountry.code,
				countryName: selectedCountry.name,
				city: city.trim() || null,
				visitDate: visitDate || null,
				notes: notes.trim() || null,
				rating: rating || null,
			});
			// Reset
			setSelectedCountry(null);
			setCity("");
			setVisitDate("");
			setNotes("");
			setRating(0);
		} finally {
			setLoading(false);
		}
	};

	const handleClose = () => {
		setSelectedCountry(null);
		setCity("");
		setVisitDate("");
		setNotes("");
		setRating(0);
		onClose();
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			maxWidth="sm"
			fullWidth
			PaperProps={{
				sx: {
					borderRadius: DASHBOARD.radiusCard,
					background: DASHBOARD.bgCard,
					overflow: "hidden",
					boxShadow: DASHBOARD.shadowMd,
					border: `1px solid ${DASHBOARD.border}`,
				},
			}}
		>
			{/* Header alineado con Dashboard */}
			<Box
				sx={{
					background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
					py: 3,
					px: 3,
					textAlign: "center",
					boxShadow: DASHBOARD.shadowPrimary,
				}}
			>
				<Typography
					sx={{
						fontSize: "0.65rem",
						color: "rgba(255,255,255,0.8)",
						textTransform: "uppercase",
						letterSpacing: "0.25em",
						mb: 0.5,
						fontWeight: 600,
					}}
				>
					Nuevo sello
				</Typography>
				<Typography
					sx={{
						fontSize: "1.25rem",
						color: "#fff",
						fontWeight: 700,
					}}
				>
					Añadir destino
				</Typography>
			</Box>

			<DialogContent sx={{ pt: 3, pb: 1, px: 3 }}>
				{/* Selector de país */}
				<Autocomplete
					options={COUNTRIES}
					getOptionLabel={(opt) => `${getFlag(opt.code)} ${opt.name}`}
					value={selectedCountry}
					onChange={(_, v) => setSelectedCountry(v)}
					renderOption={(props, option) => {
						const { key, ...restProps } = props;
						return (
							<Box
								key={key}
								component="li"
								{...restProps}
								sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1 }}
							>
								<Typography
									component="span"
									sx={{ fontSize: "1.2rem", lineHeight: 1 }}
								>
									{getFlag(option.code)}
								</Typography>
								<Typography sx={{ fontSize: "0.9rem" }}>
									{option.name}
								</Typography>
							</Box>
						);
					}}
					renderInput={(params) => (
						<TextField
							{...params}
							label="País"
							placeholder="Buscar país..."
							fullWidth
							sx={{
								mb: 2,
								"& .MuiOutlinedInput-root": {
									borderRadius: DASHBOARD.radiusButton,
									"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
										borderColor: DASHBOARD.primary,
										borderWidth: 2,
									},
								},
								"& .MuiInputLabel-root.Mui-focused": {
									color: DASHBOARD.primary,
								},
							}}
						/>
					)}
				/>

				{/* Ciudad */}
				<TextField
					label="Ciudad (opcional)"
					fullWidth
					value={city}
					onChange={(e) => setCity(e.target.value)}
					placeholder="Ej: Tokio, París, Bangkok..."
					sx={{
						mb: 2,
						"& .MuiOutlinedInput-root": {
							borderRadius: DASHBOARD.radiusButton,
							"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
								borderColor: DASHBOARD.primary,
								borderWidth: 2,
							},
						},
						"& .MuiInputLabel-root.Mui-focused": {
							color: DASHBOARD.primary,
						},
					}}
				/>

				{/* Fecha */}
				<TextField
					label="Fecha de visita (opcional)"
					type="date"
					fullWidth
					value={visitDate}
					onChange={(e) => setVisitDate(e.target.value)}
					InputLabelProps={{ shrink: true }}
					sx={{
						mb: 2,
						"& .MuiOutlinedInput-root": {
							borderRadius: DASHBOARD.radiusButton,
							"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
								borderColor: DASHBOARD.primary,
								borderWidth: 2,
							},
						},
						"& .MuiInputLabel-root.Mui-focused": {
							color: DASHBOARD.primary,
						},
					}}
				/>

				{/* Notas */}
				<TextField
					label="Notas del viaje (opcional)"
					fullWidth
					multiline
					minRows={2}
					maxRows={4}
					value={notes}
					onChange={(e) => setNotes(e.target.value)}
					placeholder="¿Qué recuerdas de este viaje?"
					sx={{
						mb: 2,
						"& .MuiOutlinedInput-root": {
							borderRadius: DASHBOARD.radiusButton,
							"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
								borderColor: DASHBOARD.primary,
								borderWidth: 2,
							},
						},
						"& .MuiInputLabel-root.Mui-focused": {
							color: DASHBOARD.primary,
						},
					}}
				/>

				{/* Rating */}
				<Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
					<Typography sx={{ fontSize: "0.85rem", color: DASHBOARD.textMuted }}>
						Valoración:
					</Typography>
					<Rating
						value={rating}
						onChange={(_, v) => setRating(v)}
						sx={{
							"& .MuiRating-iconFilled": { color: DASHBOARD.primary },
							"& .MuiRating-iconHover": { color: DASHBOARD.primaryDark },
						}}
					/>
				</Box>
			</DialogContent>

			<DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
				<Button
					onClick={handleClose}
					sx={{
						textTransform: "none",
						color: DASHBOARD.textMuted,
						borderRadius: DASHBOARD.radiusButton,
						px: 3,
						"&:hover": { bgcolor: DASHBOARD.bgSubtle, color: DASHBOARD.textPrimary },
					}}
				>
					Cancelar
				</Button>
				<Button
					onClick={handleSubmit}
					disabled={!selectedCountry || loading}
					variant="contained"
					sx={{
						textTransform: "none",
						borderRadius: DASHBOARD.radiusButton,
						px: 4,
						fontWeight: 600,
						background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
						boxShadow: DASHBOARD.shadowPrimary,
						"&:hover": {
							background: "linear-gradient(135deg, #5558E3 0%, #7C4DE8 100%)",
							boxShadow: "0 6px 20px rgba(99, 102, 241, 0.45)",
						},
						"&.Mui-disabled": {
							background: "rgba(0,0,0,0.08)",
							color: DASHBOARD.textMuted,
						},
					}}
				>
					{loading ? "Añadiendo..." : "Añadir sello"}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default AddCountryDialog;
